import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import PageHero from "@/components/PageHero";
import CTABand from "@/components/CTABand";
import LogoMarquee from "@/components/LogoMarquee";
import BrandTile from "@/components/BrandTile";
import { BRANDS } from "@/data/brands";

export default function Brands() {
  return (
    <div>
      <PageHero
        eyebrow="Brands · Dealerships"
        title="Authorized dealer for leading industrial brands."
        description="We work with reputable industrial brands as an authorized dealer, distributor or channel partner — ensuring genuine products, reliable stock and technical backing."
        image="/images/products/velvex/hydrovel-68-210l.png"
      />

      <section className="section bg-slate-50">
        <div className="container-nago">
          <div className="text-center max-w-2xl mx-auto">
            <div className="eyebrow">All brands</div>
            <h2 className="mt-3 font-display font-extrabold text-3xl sm:text-4xl text-slate-900">
              Trusted names across lubricants, tooling &amp; safety.
            </h2>
          </div>
        </div>
        <div className="mt-14">
          <LogoMarquee
            items={BRANDS}
            testId="brands-marquee-1"
            renderItem={(b) => (
              <BrandTile text={b.name} sub={b.category} image={b.logo} />
            )}
          />
        </div>
        <div className="mt-8">
          <LogoMarquee
            items={[...BRANDS].reverse()}
            speed="slow"
            testId="brands-marquee-2"
            renderItem={(b) => (
              <BrandTile text={b.name} sub={b.category} image={b.logo} />
            )}
          />
        </div>
      </section>

      {/* Brand grid — click through to detail */}
      <section className="section bg-white">
        <div className="container-nago">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
            <div>
              <div className="eyebrow">Dealership-wise catalogue</div>
              <h2 className="mt-3 font-display font-extrabold text-3xl sm:text-4xl text-slate-900">
                Explore each brand & their product range.
              </h2>
            </div>
            <div className="text-sm text-slate-600">
              {BRANDS.length} authorized brands ·{" "}
              {BRANDS.reduce((n, b) => n + (b.products?.length || 0), 0)}+
              products
            </div>
          </div>

          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {BRANDS.map((b) => (
              <Link
                key={b.id}
                to={`/brands/${b.id}`}
                className="group card-industrial p-0 overflow-hidden flex flex-col"
                data-testid={`brand-card-${b.id}`}
              >
                <div className="relative aspect-[16/9] bg-white border-b border-slate-200 flex items-center justify-center p-6">
                  <img
                    src={b.logo}
                    alt={`${b.name} logo`}
                    className="h-48 aspect-3/2 max-w-full object-contain group-hover:scale-105"
                    style={{
                      transition: "transform 400ms ease",
                    }}
                    loading="lazy"
                  />
                </div>
                <div className="p-6 flex-1 flex flex-col">
                  <div className="text-xs uppercase tracking-widest text-[#E30613] font-bold">
                    {b.category}
                  </div>
                  <h3 className="mt-2 font-display font-bold text-lg text-slate-900">
                    {b.name}
                  </h3>
                  {b.parent && (
                    <div className="mt-1 text-xs text-slate-500">
                      Powered by {b.parent}
                    </div>
                  )}
                  <p className="mt-3 text-sm text-slate-600 leading-relaxed flex-1">
                    {b.tagline}
                  </p>
                  <div className="mt-4 flex items-center justify-between text-xs">
                    <span className="text-slate-500">
                      {b.products?.length
                        ? `${b.products.length} products`
                        : "Enquire for range"}
                    </span>
                    <span className="inline-flex items-center font-semibold text-[#1E3A8A] group-hover:text-[#E30613]">
                      View details <ArrowRight className="w-3.5 h-3.5 ml-1" />
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <CTABand
        title="Looking for a specific brand or grade?"
        subtitle="If you need a brand not listed here, let us know — we may still be able to source it through our partner network."
      />
    </div>
  );
}
